import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Outlet, useNavigate, useSearchParams } from 'react-router-dom'

import s from './MyCampaigns.module.scss'

import MyCampaignCard from './components/MyCampaignCard'

import { METAVERSE_IMAGE_BY_NAME_FILTER } from 'constants/MetaverseIcons'
import {
  CAMPAIGN_STATUS_ICON_BY_VALUE_FILTER,
  STATUS_FILTER_OPTIONS,
  STATUS_NAME_BY_STATUS_VALUE,
} from 'constants/campaignStatusConsts'

import { useCreateMetaOpts } from 'hooks/useCreateMetaOpts'
import { usePagination } from 'hooks/usePagination'

import { useAppErrorContext } from 'contexts/AppErrorContext'
import { useAppSuccessMessageContext } from 'contexts/SuccessMessageContext'

import { campaignsApi } from 'api/campaigns-api'

import { GlobeIcon } from 'assets/icons/GlobeIcon'
import { InfoIcon } from 'assets/icons/InfoIcon'
import publisher from 'assets/svg/briefcase.svg'

import { SelectControlled } from 'components/Controls/Select/SelectControlled'
import { BurgerWrapper } from 'components/UI/BurgerWrapper/BurgerWrapper'
import { CustomButton } from 'components/UI/CustomButton/CustomButton'
import Header from 'components/UI/Header'
import { NoContent } from 'components/UI/NoContent/NoContent'
import PageTitle from 'components/UI/PageTitle'
import Search from 'components/UI/Search'

const MyCampaigns = () => {
  const { setAppError } = useAppErrorContext()
  const { setSuccessMessage } = useAppSuccessMessageContext()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const { filterMetaverseOptions } = useCreateMetaOpts()

  const { control, setValue } = useForm({
    mode: 'onBlur',
    defaultValues: {
      selectStatuses: STATUS_FILTER_OPTIONS[0],
      selectMetaverses: filterMetaverseOptions[0],
    },
  })

  const {
    data: campaigns,
    loading,
    totalElements,
    paging,
    setPaging,
    forceUpdate,
    handleFilterChange,
    handleDelete,
    deleteLoading,
  } = usePagination({
    getData: campaignsApi.paging,
    formValueSetter: setValue,
    deleteData: campaignsApi.delete,
  })

  //sets select value if query not empty on page open
  const fStatus = searchParams.get('f__status') || ''
  useEffect(() => {
    if (fStatus)
      setValue('selectStatuses', { value: fStatus, label: (STATUS_NAME_BY_STATUS_VALUE as any)[fStatus.split('-')[0]] })
  }, [])

  const fMetaverse = searchParams.get('f__ad_campaign_places.ad_spot.spot_type') || ''
  useEffect(() => {
    if (fMetaverse) setValue('selectMetaverses', { value: fMetaverse, label: fMetaverse.split('-')[0] })
  }, [])

  //after creation of campaign
  const createdCampaignId = localStorage.getItem('createdCampaignId') || 0
  const campStarted = localStorage.getItem('started') || ''

  useEffect(() => {
    const start = async () => {
      if (createdCampaignId && campStarted === 'started') {
        localStorage.removeItem('createdCampaignId')
        localStorage.removeItem('started')
        try {
          const res = await campaignsApi.start(+createdCampaignId)
          setPaging((prev) => ({ ...prev, page: { ...prev.page, page_number: 0 } }))
          forceUpdate()
          if (res.status === 200) setSuccessMessage('The campaign has been successfully created')
        } catch (error: any) {
          setAppError(error)
        }
      }
    }
    start()
  }, [createdCampaignId, campStarted])

  const title =
    paging?.quick_search?.length || paging?.filters?.length
      ? campaigns?.length
        ? 'Found Campaigns'
        : 'Nothing found'
      : campaigns?.length
      ? 'My Campaigns'
      : null

  return (
    <>
      <Outlet />
      <Header>
        <div className={s.headerWrapper}>
          <CustomButton
            variant="secondary"
            onClick={() => {
              localStorage.removeItem('createdCampaignId')
              navigate('../advertiser/create-campaign')
            }}
            size="240"
            className={s.button}
          >
            New Campaign
          </CustomButton>
          <BurgerWrapper>
            <SelectControlled
              control={control}
              name="selectStatuses"
              options={STATUS_FILTER_OPTIONS}
              optionsIcons={CAMPAIGN_STATUS_ICON_BY_VALUE_FILTER}
              className={s.selectStatus}
              placeholder={STATUS_FILTER_OPTIONS[0].label}
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'selectStatuses',
                  searchParamName: 'f__status',
                  option: opt,
                })
              }
              icon={<InfoIcon />}
            />
            <SelectControlled
              control={control}
              name="selectMetaverses"
              options={filterMetaverseOptions}
              optionsIcons={METAVERSE_IMAGE_BY_NAME_FILTER}
              className={s.selectMeta}
              placeholder="All Metaverses"
              onChange={(opt) =>
                handleFilterChange({
                  fieldName: 'selectMetaverses',
                  searchParamName: 'f__ad_campaign_places.ad_spot.spot_type',
                  option: opt,
                })
              }
              icon={<GlobeIcon />}
            />
            <Search paramPrefix="campaign" className={s.search} />
          </BurgerWrapper>
        </div>
        <PageTitle title={title} count={totalElements} color="secondary" loading={loading || deleteLoading} />
      </Header>
      <main>
        {campaigns?.length ? (
          <div className={s.cardsWrapper}>
            {campaigns.map((campaign) => (
              <MyCampaignCard key={campaign.id} campaign={campaign} deleteCampaign={handleDelete} />
            ))}
          </div>
        ) : !paging?.quick_search?.length && !paging?.filters?.length && !loading ? (
          <NoContent icon={publisher} title="No Campaigns" subtitle="You haven’t created any campaigns yet." />
        ) : null}
      </main>
    </>
  )
}

export default MyCampaigns
